import { Injectable, NgZone } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'src/environments/environment';
import { Usuario } from '../interfaces/usuario/usuario.interface';
import { UsuarioService } from './usuario.service';

/** Validade do access token (epoch ms, relógio local), gravada junto do token. */
export const CHAVE_TOKEN_EXPIRA_EM = 'tokenExpiraEm';

/**
 * Sessão persistente do web: access token curto em `localStorage['token']` e refresh token em
 * cookie HttpOnly da API (o JavaScript não o lê; vai sozinho com `withCredentials` para
 * `/v1/auth`).
 *
 * - **Uma renovação por vez**, também entre abas: a API troca o refresh token a cada uso, e duas
 *   renovações com o mesmo cookie derrubariam uma aba. Dentro da aba, a mesma `Promise`; entre
 *   abas, a Web Locks API. Quem esperou a trava e acha um token novo no storage usa esse.
 * - **Só a recusa (401) encerra a sessão.** Falha de rede na renovação não desloga: a chamada
 *   falha como falharia sem sessão nenhuma.
 * - As chamadas daqui usam um `HttpClient` sem interceptor (`HttpBackend`), para a renovação não
 *   passar pela renovação.
 */
@Injectable({
  providedIn: 'root'
})
export class SessaoService {
  /** Renova quando faltar menos que isto para o token vencer. */
  private static readonly MARGEM_MS = 60_000;
  private static readonly TRAVA = 'fragmaq-sessao';

  private readonly http: HttpClient;
  private readonly baseAuthUrl = `${environment.APIEndpoint}auth`;
  private renovacao: Promise<string | null> | null = null;
  private avisoExpiradaMostrado = false;

  constructor(
    httpBackend: HttpBackend,
    private usuarioService: UsuarioService,
    private router: Router,
    private toastr: ToastrService,
    private zone: NgZone
  ) {
    this.http = new HttpClient(httpBackend);
    window.addEventListener('storage', (evento) => this.zone.run(() => this.aoMudarEmOutraAba(evento)));
  }

  token(): string | null {
    return localStorage.getItem('token');
  }

  /** Grava o token do login ou da renovação. Sem a validade (API antiga), usa o `exp` do JWT. */
  guardarToken(token: string, expiraEmSegundos?: number | null): void {
    localStorage.setItem('token', token);

    const expiraEm = expiraEmSegundos && expiraEmSegundos > 0
      ? Date.now() + expiraEmSegundos * 1000
      : this.expiracaoDoJwt(token);

    if (expiraEm) localStorage.setItem(CHAVE_TOKEN_EXPIRA_EM, String(expiraEm));
    else localStorage.removeItem(CHAVE_TOKEN_EXPIRA_EM);

    this.avisoExpiradaMostrado = false;
  }

  /** O token vence (ou venceu) dentro da margem. Sem token, falso: não há o que renovar. */
  precisaRenovar(): boolean {
    const token = this.token();
    if (!token) return false;

    const gravada = Number(localStorage.getItem(CHAVE_TOKEN_EXPIRA_EM));
    const expiraEm = gravada > 0 ? gravada : this.expiracaoDoJwt(token);
    return !expiraEm || Date.now() >= expiraEm - SessaoService.MARGEM_MS;
  }

  /**
   * Token para mandar agora: o atual, ou um renovado se estiver vencendo. Nulo se a sessão acabou.
   * Falha de rede na renovação devolve o atual (a chamada decide).
   */
  async tokenParaEnvio(): Promise<string | null> {
    const token = this.token();
    if (!token || !this.precisaRenovar()) return token;

    try {
      return await this.renovar();
    } catch {
      return token;
    }
  }

  /**
   * Troca o refresh token (cookie) por um token novo. Nulo = sessão recusada (já levou ao login);
   * exceção = falha de rede ou da API (sessão continua).
   */
  renovar(): Promise<string | null> {
    if (!this.renovacao) {
      const tokenAntes = this.token();
      this.renovacao = this.comTrava(() => this.renovarNaApi(tokenAntes))
        .finally(() => this.renovacao = null);
    }
    return this.renovacao;
  }

  /** Sair: revoga a sessão na API (cookie) e limpa o navegador, sem esperar a resposta. */
  encerrar(): void {
    firstValueFrom(this.http.post(`${this.baseAuthUrl}/logout`, {}, { withCredentials: true }))
      .catch(() => { /* a sessão vence sozinha; o navegador já foi limpo */ });
    this.usuarioService.logout();
  }

  private async renovarNaApi(tokenAntes: string | null): Promise<string | null> {
    // Outra aba renovou enquanto esta esperava a trava: o cookie já é o novo, e o token dela serve.
    const atual = this.token();
    if (atual && atual !== tokenAntes && !this.precisaRenovar()) return atual;

    let resposta: any;
    try {
      resposta = await firstValueFrom(
        this.http.post<any>(`${this.baseAuthUrl}/refresh`, {}, { withCredentials: true }));
    } catch (erro: any) {
      if (erro?.status === 401) {
        this.expirar();
        return null;
      }
      throw erro;
    }

    if (!resposta?.token) {
      this.expirar();
      return null;
    }

    this.guardarToken(resposta.token, resposta.expiraEmSegundos);
    await this.recarregarUsuario(resposta.token);
    return resposta.token;
  }

  /**
   * Menus e permissões vêm do `GET usuarios/{id}`, que antes só rodava no login. Com a sessão
   * longa, recarregar a cada renovação faz tela liberada aparecer sem sair e entrar. Só emite o
   * usuário logado quando algo que a tela usa mudou (o sidebar se refaz a cada emissão).
   */
  private async recarregarUsuario(token: string): Promise<void> {
    try {
      const sub = (jwtDecode(token) as any)?.sub;
      if (!sub) return;

      const usuario = await firstValueFrom(this.http.get<Usuario>(
        `${environment.APIEndpoint}usuarios/${sub}`,
        { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) }));
      if (!usuario) return;

      const anterior = this.lerUsuarioGravado();
      localStorage.setItem('usuario', JSON.stringify(usuario));

      const assinatura = (u: any) => JSON.stringify([
        u?.id, u?.nome, u?.email, u?.imagemPerfil, u?.menus, u?.permissoes, u?.setoresAprovador]);

      if (this.usuarioService.getUsuarioLogado().value && assinatura(anterior) !== assinatura(usuario)) {
        this.usuarioService.setUsuarioLogado(usuario.id, usuario.nome, usuario.email, usuario.permissoes,
          usuario.imagemPerfil, usuario.menus, usuario.setoresAprovador, usuario.chamadosNaoVisualizados);
      }
    } catch {
      // Acessório: sem ele o usuário segue com os menus que já tinha.
    }
  }

  /** Sessão recusada pela API: limpa e leva ao login, com um aviso só. */
  private expirar(): void {
    this.usuarioService.logout();

    if (!this.avisoExpiradaMostrado) {
      this.avisoExpiradaMostrado = true;
      this.toastr.warning('Sua sessão expirou. Entre de novo.', 'Sessão');
    }

    if (!window.location.pathname.includes('/autenticacao/')) {
      this.router.navigateByUrl('/autenticacao/login');
    }
  }

  /**
   * Outra aba saiu (token apagado): esta vai para o login. Outra aba entrou com outro usuário:
   * recarrega, para esta não seguir com o usuário anterior em memória.
   */
  private aoMudarEmOutraAba(evento: StorageEvent): void {
    if (evento.storageArea !== localStorage) return;
    if (evento.key !== 'token' && evento.key !== null) return;

    const novo = this.token();
    if (!novo) {
      if (this.usuarioService.getUsuarioLogado().value) this.usuarioService.logout();
      if (!window.location.pathname.includes('/autenticacao/')) {
        this.router.navigateByUrl('/autenticacao/login');
      }
      return;
    }

    const idEmUso = this.usuarioService.getUsuarioLogado().value?.id;
    const idNovo = Number((this.decodificar(novo) as any)?.sub);
    if (idEmUso && idNovo && idEmUso !== idNovo) {
      window.location.reload();
    }
  }

  private async comTrava<T>(acao: () => Promise<T>): Promise<T> {
    const locks = (navigator as any).locks;
    if (!locks?.request) return acao();
    return locks.request(SessaoService.TRAVA, acao);
  }

  private lerUsuarioGravado(): any {
    try {
      return JSON.parse(localStorage.getItem('usuario') ?? 'null');
    } catch {
      return null;
    }
  }

  private expiracaoDoJwt(token: string): number | null {
    const exp = (this.decodificar(token) as any)?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  }

  private decodificar(token: string): unknown {
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }
}
